// Dynamic Pricing Service
// Implements pricing logic from STRIPE_PRODUCT_STRATEGY.md

export interface PricingTier {
  id: string;
  name: string;
  description: string;
  minPhotos: number;
  maxPhotos?: number;
  basePrice: number; // Price per photo in cents
  discount: number; // Percentage discount (0-1)
  savings?: number; // Calculated savings amount
}

export interface BundleOption {
  id: string;
  name: string;
  description: string;
  photoCount: number;
  price: number; // Total price in cents
  originalPrice: number; // Price without bundle discount
  discount: number; // Percentage saved
  savings: number; // Amount saved in cents
  popular?: boolean;
}

export interface PricingCalculation {
  subtotal: number; // Base price before discounts
  volumeDiscount: number; // Volume discount amount
  promoDiscount: number; // Promo code discount amount
  total: number; // Final price
  savings: number; // Total savings
  effectiveRate: number; // Price per photo after discounts
  appliedTier?: PricingTier;
  appliedPromo?: string;
}

export interface EventPricing {
  eventId: string;
  eventType: 'wedding' | 'corporate' | 'birthday' | 'sports' | 'other';
  basePrice: number; // Base price per photo in cents
  multiplier: number; // Event type multiplier
  volumeDiscounts: VolumeDiscount[];
  bundleOptions: BundleOption[];
  promoCode?: PromoCode;
}

export interface VolumeDiscount {
  minPhotos: number;
  discount: number; // Percentage (0-1)
  name: string;
}

export interface PromoCode {
  code: string;
  discount: number; // Percentage (0-1) or fixed amount
  type: 'percentage' | 'fixed';
  minPurchase?: number;
  maxDiscount?: number;
  validUntil?: Date;
  description: string;
}

export class PricingService {
  // Base pricing configuration from strategy document
  private static readonly BASE_PRICES = {
    single_photo: 500, // $5.00
    small_bundle: 1500, // $15.00 for 5 photos
    medium_bundle: 2500, // $25.00 for 10 photos
    large_bundle: 4000, // $40.00 for 20 photos
    full_event: 7500, // $75.00 for all photos
  };

  private static readonly VOLUME_DISCOUNTS: VolumeDiscount[] = [
    { minPhotos: 5, discount: 0.1, name: '5+ Photos - 10% off' },
    { minPhotos: 10, discount: 0.15, name: '10+ Photos - 15% off' },
    { minPhotos: 20, discount: 0.2, name: '20+ Photos - 20% off' },
    { minPhotos: 50, discount: 0.25, name: '50+ Photos - 25% off' },
  ];

  static readonly EVENT_TYPE_MULTIPLIERS = {
    wedding: 1.5, // Premium events
    corporate: 1.3, // Business events
    birthday: 1.0, // Standard events
    sports: 0.9, // High-volume events
    other: 1.0,
  };

  /**
   * Calculate dynamic pricing for a given number of photos
   */
  static calculatePrice(
    photoCount: number,
    eventPricing?: EventPricing,
    promoCode?: string
  ): PricingCalculation {
    const basePrice = eventPricing?.basePrice || this.BASE_PRICES.single_photo;
    const multiplier = eventPricing?.multiplier || 1.0;
    const adjustedBasePrice = basePrice * multiplier;

    // Calculate subtotal
    const subtotal = adjustedBasePrice * photoCount;

    // Find applicable volume discount
    const volumeDiscounts = eventPricing?.volumeDiscounts || this.VOLUME_DISCOUNTS;
    const appliedVolumeDiscount = this.findApplicableVolumeDiscount(photoCount, volumeDiscounts);
    const volumeDiscount = appliedVolumeDiscount ? subtotal * appliedVolumeDiscount.discount : 0;

    // Convert to PricingTier format
    const appliedTier = appliedVolumeDiscount
      ? {
          id: `tier_${appliedVolumeDiscount.minPhotos}`,
          name: appliedVolumeDiscount.name,
          description: `${Math.round(appliedVolumeDiscount.discount * 100)}% off per photo`,
          minPhotos: appliedVolumeDiscount.minPhotos,
          basePrice: adjustedBasePrice * (1 - appliedVolumeDiscount.discount),
          discount: appliedVolumeDiscount.discount,
        }
      : undefined;

    // Calculate promo discount
    let promoDiscount = 0;
    let appliedPromo: string | undefined;
    if (promoCode && eventPricing?.promoCode?.code === promoCode) {
      const promo = eventPricing.promoCode;
      if (promo.type === 'percentage') {
        promoDiscount = (subtotal - volumeDiscount) * promo.discount;
        if (promo.maxDiscount) {
          promoDiscount = Math.min(promoDiscount, promo.maxDiscount);
        }
      } else {
        promoDiscount = promo.discount;
      }
      appliedPromo = promoCode;
    }

    // Calculate final total
    const total = Math.max(0, subtotal - volumeDiscount - promoDiscount);
    const savings = subtotal - total;
    const effectiveRate = photoCount > 0 ? total / photoCount : 0;

    return {
      subtotal,
      volumeDiscount,
      promoDiscount,
      total,
      savings,
      effectiveRate,
      appliedTier,
      appliedPromo,
    };
  }

  /**
   * Generate bundle options for an event
   */
  static generateBundleOptions(totalPhotos: number, eventPricing?: EventPricing): BundleOption[] {
    const basePrice = eventPricing?.basePrice || this.BASE_PRICES.single_photo;
    const multiplier = eventPricing?.multiplier || 1.0;
    const adjustedBasePrice = basePrice * multiplier;

    const bundles: BundleOption[] = [];

    // Predefined bundle tiers
    const bundleTiers = [
      {
        photoCount: 5,
        name: 'Starter Bundle',
        description: 'Perfect for highlights',
        popular: false,
      },
      {
        photoCount: 10,
        name: 'Essential Bundle',
        description: 'Great value selection',
        popular: true,
      },
      {
        photoCount: 20,
        name: 'Premium Bundle',
        description: 'Comprehensive collection',
        popular: false,
      },
      {
        photoCount: Math.min(50, totalPhotos),
        name: 'Deluxe Bundle',
        description: 'Extended collection',
        popular: false,
      },
    ];

    // Add full event bundle if more than 50 photos
    if (totalPhotos > 50) {
      bundleTiers.push({
        photoCount: totalPhotos,
        name: 'Complete Collection',
        description: 'Every photo from the event',
        popular: false,
      });
    }

    bundleTiers.forEach((tier, _index) => {
      if (tier.photoCount <= totalPhotos) {
        const originalPrice = adjustedBasePrice * tier.photoCount;
        const pricing = this.calculatePrice(tier.photoCount, eventPricing);

        bundles.push({
          id: `bundle_${tier.photoCount}`,
          name: tier.name,
          description: tier.description,
          photoCount: tier.photoCount,
          price: pricing.total,
          originalPrice,
          discount: pricing.appliedTier?.discount || 0,
          savings: pricing.savings,
          popular: tier.popular,
        });
      }
    });

    return bundles.filter(bundle => bundle.photoCount >= 5);
  }

  /**
   * Find the best applicable volume discount
   */
  private static findApplicableVolumeDiscount(
    photoCount: number,
    volumeDiscounts: VolumeDiscount[]
  ): VolumeDiscount | undefined {
    const applicable = volumeDiscounts
      .filter(discount => photoCount >= discount.minPhotos)
      .sort((a, b) => b.discount - a.discount); // Sort by highest discount first

    return applicable[0] || undefined;
  }

  /**
   * Get pricing tiers for display
   */
  static getPricingTiers(eventPricing?: EventPricing): PricingTier[] {
    const basePrice = eventPricing?.basePrice || this.BASE_PRICES.single_photo;
    const multiplier = eventPricing?.multiplier || 1.0;
    const adjustedBasePrice = basePrice * multiplier;
    const volumeDiscounts = eventPricing?.volumeDiscounts || this.VOLUME_DISCOUNTS;

    const tiers: PricingTier[] = [
      {
        id: 'single',
        name: 'Single Photo',
        description: 'Per photo pricing',
        minPhotos: 1,
        maxPhotos: 4,
        basePrice: adjustedBasePrice,
        discount: 0,
      },
    ];

    volumeDiscounts.forEach((discount, index) => {
      const nextDiscount = volumeDiscounts[index + 1];
      tiers.push({
        id: `tier_${discount.minPhotos}`,
        name: discount.name,
        description: `${Math.round(discount.discount * 100)}% off per photo`,
        minPhotos: discount.minPhotos,
        maxPhotos: nextDiscount?.minPhotos ? nextDiscount.minPhotos - 1 : undefined,
        basePrice: adjustedBasePrice * (1 - discount.discount),
        discount: discount.discount,
      });
    });

    return tiers;
  }

  /**
   * Format currency amount
   */
  static formatCurrency(cents: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  }

  /**
   * Get the next pricing tier threshold
   */
  static getNextTierInfo(
    currentPhotoCount: number,
    eventPricing?: EventPricing
  ): { photosNeeded: number; tierName: string; discount: number } | null {
    const volumeDiscounts = eventPricing?.volumeDiscounts || this.VOLUME_DISCOUNTS;

    const nextTier = volumeDiscounts.find(discount => discount.minPhotos > currentPhotoCount);

    if (!nextTier) return null;

    return {
      photosNeeded: nextTier.minPhotos - currentPhotoCount,
      tierName: nextTier.name,
      discount: nextTier.discount,
    };
  }

  /**
   * Validate and apply promo code
   */
  static validatePromoCode(
    code: string,
    subtotal: number,
    eventPricing?: EventPricing
  ): { valid: boolean; discount: number; message: string } {
    const promo = eventPricing?.promoCode;

    if (!promo || promo.code.toLowerCase() !== code.toLowerCase()) {
      return { valid: false, discount: 0, message: 'Invalid promo code' };
    }

    if (promo.validUntil && new Date() > promo.validUntil) {
      return { valid: false, discount: 0, message: 'Promo code has expired' };
    }

    if (promo.minPurchase && subtotal < promo.minPurchase) {
      return {
        valid: false,
        discount: 0,
        message: `Minimum purchase of ${this.formatCurrency(promo.minPurchase)} required`,
      };
    }

    let discount = 0;
    if (promo.type === 'percentage') {
      discount = subtotal * promo.discount;
      if (promo.maxDiscount) {
        discount = Math.min(discount, promo.maxDiscount);
      }
    } else {
      discount = promo.discount;
    }

    return {
      valid: true,
      discount,
      message: `${promo.description} applied!`,
    };
  }

  /**
   * Create Stripe line items for dynamic pricing
   */
  static createStripeLineItems(
    eventName: string,
    photoCount: number,
    eventPricing?: EventPricing,
    promoCode?: string
  ) {
    const pricing = this.calculatePrice(photoCount, eventPricing, promoCode);

    return [
      {
        price_data: {
          currency: 'usd',
          product_data: {
            name: `${eventName} - Photo Package`,
            description: `${photoCount} high-resolution photos`,
            metadata: {
              photo_count: photoCount.toString(),
              event_pricing: eventPricing ? JSON.stringify(eventPricing) : '',
              promo_code: promoCode || '',
            },
          },
          unit_amount: pricing.total,
        },
        quantity: 1,
      },
    ];
  }
}
